import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

let cartBusy = false;

/**
 * Homepage programme cards, stock refresh, native cart actions and a flyer viewer.
 * @extends {Component}
 */
class MindxbridgeProjects extends Component {
  #controller;
  #observer;
  #timer;
  #refreshing;
  #messages = {};
  #busy = false;
  #opener;
  #bodyOverflow;
  #zoom = 1;
  #baseWidth = 0;
  #points = new Map();
  #pinchDistance = 0;
  #pinchZoom = 1;

  connectedCallback() {
    super.connectedCallback();
    this.#initialise();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#initialise();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #initialise() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const messages = this.querySelector('[data-mxb-messages]');
    this.#messages = messages ? JSON.parse(messages.textContent) : {};
    this.addEventListener('submit', this.#submit, { signal });
    this.addEventListener('change', this.#change, { signal });
    this.addEventListener('click', this.#click, { signal });
    const dialog = this.querySelector('dialog');
    dialog?.addEventListener('close', this.#closeViewer, { signal });
    const stage = this.querySelector('.mxb-projects__viewer-stage');
    stage?.addEventListener('pointerdown', this.#pointerDown, { signal });
    stage?.addEventListener('pointermove', this.#pointerMove, { signal });
    stage?.addEventListener('pointerup', this.#pointerUp, { signal });
    stage?.addEventListener('pointercancel', this.#pointerUp, { signal });
    stage?.addEventListener('keydown', this.#panKeys, { signal });
    stage?.querySelector('img')?.addEventListener('load', () => this.#fitImage(), { signal });
    window.addEventListener('resize', () => this.#fitImage(), { signal });
    window.addEventListener('focus', () => this.#refresh().catch(() => {}), { signal });
    for (const card of this.#cards()) this.#updateCard(card);
    if (!this.#cards().length) return;
    this.#observer = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting);
      if (visible && !this.#timer) {
        this.#timer = setInterval(() => {
          if (!document.hidden && !this.#busy) this.#refresh().catch(() => {});
        }, 60000);
      } else if (!visible && this.#timer) {
        clearInterval(this.#timer);
        this.#timer = undefined;
      }
    });
    this.#observer.observe(this);
  }

  #cleanup() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    clearInterval(this.#timer);
    this.#timer = undefined;
    this.#refreshing = undefined;
    this.querySelector('dialog')?.close();
    this.#closeViewer();
  }

  #cards() {
    return this.querySelectorAll('.mxb-projects__card');
  }

  #variant(card) {
    const input = card.querySelector('[name="id"]');
    const data = card.querySelector('[data-mxb-variants]');
    if (!input || !data) return undefined;
    return JSON.parse(data.textContent).find((item) => String(item.id) === input.value);
  }

  #updateCard(card) {
    const variant = this.#variant(card);
    if (!variant) return;
    card.dataset.available = String(variant.available);
    const price = card.querySelector('[data-mxb-price]');
    if (price) price.textContent = variant.price;
    const seats = card.querySelector('[data-mxb-seats]');
    if (seats) {
      seats.textContent = !variant.available ? this.#messages.soldOut
        : variant.tracked && variant.seats > 0
          ? variant.seats === 1 ? this.#messages.oneSeat : this.#messages.seats.replace('__COUNT__', String(variant.seats))
          : this.#messages.available;
    }
    const consent = card.querySelector('[data-mxb-consent]');
    if (consent) {
      consent.disabled = this.#busy || !variant.available || !variant.supported;
      if (!variant.available || !variant.supported) consent.checked = false;
    }
    for (const button of card.querySelectorAll('button[type="submit"]')) {
      button.disabled = this.#busy || !variant.available || !variant.supported || !consent?.checked;
    }
    const selector = card.querySelector('select[name="id"]');
    if (selector) selector.disabled = this.#busy;
    const checkout = card.querySelector('[data-mxb-checkout]');
    if (checkout) checkout.disabled = this.#busy || !consent?.checked;
  }

  #change = (event) => {
    if (!(event.target instanceof Element)) return;
    const card = event.target.closest('.mxb-projects__card');
    if (!card) return;
    this.#updateCard(card);
    if (event.target.matches('select[name="id"]')) {
      this.#setStatus(card, this.#variant(card)?.supported ? '' : this.#messages.optionsRequired);
      card.querySelector('[data-mxb-checkout]').hidden = true;
    }
  };

  #setStatus(card, message) {
    const status = card.querySelector('[data-mxb-status]');
    if (status) status.textContent = message;
  }

  async #request(url, options = {}) {
    const signal = AbortSignal.any([this.#controller.signal, AbortSignal.timeout(15000)]);
    const response = await fetch(url, { credentials: 'same-origin', ...options, signal });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || this.#messages.error);
    return data;
  }

  async #refresh() {
    if (this.#refreshing) return this.#refreshing;
    const controller = this.#controller;
    const refresh = (async () => {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.dataset.sectionId);
      const signal = AbortSignal.any([controller.signal, AbortSignal.timeout(15000)]);
      const response = await fetch(url, { signal, credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error(this.#messages.unconfirmed);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const freshCards = html.querySelectorAll('.mxb-projects__card');
      if (!freshCards.length && this.#cards().length) throw new Error(this.#messages.unconfirmed);
      for (const card of this.#cards()) {
        const fresh = Array.from(freshCards).find((item) => item.dataset.cardKey === card.dataset.cardKey && item.dataset.productId === card.dataset.productId);
        const variants = fresh?.querySelector('[data-mxb-variants]');
        if (variants) {
          card.querySelector('[data-mxb-variants]').textContent = variants.textContent;
          this.#updateCard(card);
        }
      }
      return new Set(Array.from(freshCards, (card) => card.dataset.cardKey));
    })();
    this.#refreshing = refresh;
    try { return await refresh; }
    finally { if (this.#refreshing === refresh) this.#refreshing = undefined; }
  }

  #submit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.mxb-projects__form')) return;
    event.preventDefault();
    const card = form.closest('.mxb-projects__card');
    if (!card || !form.reportValidity()) return;
    const consent = form.querySelector('[data-mxb-consent]');
    const variant = this.#variant(card);
    if (!consent?.checked || !variant?.available || !variant?.supported) return;
    if (cartBusy) { this.#setStatus(card, this.#messages.working); return; }
    const action = event.submitter?.value === 'buy' ? 'buy' : 'add';
    const variantId = String(variant.id);
    cartBusy = true;
    this.#busy = true;
    for (const item of this.#cards()) this.#updateCard(item);
    this.#setStatus(card, this.#messages.working);
    try {
      const freshKeys = await this.#refresh();
      const freshVariant = this.#variant(card);
      if (!freshKeys.has(card.dataset.cardKey) || !freshVariant?.available || !freshVariant?.supported || String(freshVariant.id) !== variantId) {
        throw new Error(this.#messages.unavailable);
      }
      const root = window.Shopify?.routes?.root || '/';
      const cart = await this.#request(root + 'cart.js');
      const existing = cart.items?.find((item) => String(item.variant_id) === variantId && item.quantity > 0);
      if (existing) {
        this.#setStatus(card, this.#messages.alreadyAdded);
      } else {
        const body = new FormData(form);
        body.delete('mxb_action');
        body.set('id', variantId);
        const sections = Array.from(document.querySelectorAll('cart-items-component'), (item) => item.dataset.sectionId).filter(Boolean);
        if (sections.length) body.set('sections', Array.from(new Set(sections)).slice(0, 5).join(','));
        body.set('sections_url', window.location.pathname);
        const added = await this.#request(root + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body });
        this.#setStatus(card, this.#messages.added);
        if (action === 'add') {
          this.dispatchEvent(new CartAddEvent({}, variantId, {
            source: 'product-form-component', itemCount: 1,
            productId: card.dataset.productId, variantId, sections: added.sections
          }));
        }
      }
      const checkout = card.querySelector('[data-mxb-checkout]');
      if (checkout) checkout.hidden = false;
      if (action === 'buy') this.#checkout();
    } catch (error) {
      if (!this.#controller.signal.aborted) {
        this.#setStatus(card, error.message || this.#messages.error);
        this.#refresh().catch(() => {});
      }
    } finally {
      cartBusy = false;
      this.#busy = false;
      if (this.isConnected) for (const item of this.#cards()) this.#updateCard(item);
    }
  };

  #checkout() {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = this.dataset.cartUrl;
    form.hidden = true;
    const checkout = document.createElement('input');
    checkout.type = 'hidden';
    checkout.name = 'checkout';
    checkout.value = 'Checkout';
    form.append(checkout);
    document.body.append(form);
    form.submit();
    form.remove();
  }

  #click = (event) => {
    if (!(event.target instanceof Element)) return;
    const imageButton = event.target.closest('[data-mxb-image]');
    if (imageButton) { this.#openViewer(imageButton); return; }
    if (event.target.closest('[data-mxb-close]')) { this.querySelector('dialog')?.close(); return; }
    const zoomButton = event.target.closest('[data-mxb-zoom]');
    if (zoomButton) { this.#setZoom(this.#zoom + Number(zoomButton.dataset.mxbZoom)); return; }
    if (event.target.closest('[data-mxb-reset]')) { this.#setZoom(1); return; }
    if (event.target.closest('[data-mxb-checkout]')) {
      const card = event.target.closest('.mxb-projects__card');
      if (card?.querySelector('[data-mxb-consent]')?.checked && !cartBusy) this.#checkout();
    }
  };

  #openViewer(button) {
    const dialog = this.querySelector('dialog');
    const image = dialog?.querySelector('img');
    if (!dialog || !image) return;
    this.#opener = button;
    this.#zoom = 1;
    image.alt = button.dataset.imageAlt || '';
    image.src = button.dataset.mxbImage;
    this.#bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.showModal();
    this.#fitImage();
  }

  #closeViewer = () => {
    if (this.#bodyOverflow !== undefined) {
      document.body.style.overflow = this.#bodyOverflow;
      this.#bodyOverflow = undefined;
    }
    this.#points.clear();
    this.#opener?.focus({ preventScroll: true });
    this.#opener = undefined;
  };

  #fitImage() {
    const dialog = this.querySelector('dialog');
    const stage = dialog?.querySelector('.mxb-projects__viewer-stage');
    const image = stage?.querySelector('img');
    if (!dialog?.open || !image?.naturalWidth || !stage.clientHeight) return;
    this.#baseWidth = Math.min(stage.clientWidth, stage.clientHeight * image.naturalWidth / image.naturalHeight);
    this.#setZoom(this.#zoom);
  }

  #setZoom(value) {
    const stage = this.querySelector('.mxb-projects__viewer-stage');
    const image = stage?.querySelector('img');
    if (!stage || !image || !this.#baseWidth) return;
    const oldWidth = this.#baseWidth * this.#zoom;
    const centreX = (stage.scrollLeft + stage.clientWidth / 2) / oldWidth;
    const centreY = (stage.scrollTop + stage.clientHeight / 2) / (oldWidth * image.naturalHeight / image.naturalWidth);
    this.#zoom = Math.min(4, Math.max(1, value));
    image.style.width = this.#baseWidth * this.#zoom + 'px';
    stage.scrollLeft = centreX * this.#baseWidth * this.#zoom - stage.clientWidth / 2;
    stage.scrollTop = centreY * this.#baseWidth * this.#zoom * image.naturalHeight / image.naturalWidth - stage.clientHeight / 2;
    const output = this.querySelector('[data-mxb-zoom-status]');
    if (output) output.textContent = this.#messages.zoom.replace('__PERCENT__', String(Math.round(this.#zoom * 100)));
    for (const button of this.querySelectorAll('[data-mxb-zoom]')) {
      button.disabled = Number(button.dataset.mxbZoom) < 0 ? this.#zoom <= 1 : this.#zoom >= 4;
    }
  }

  #pointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.#points.set(event.pointerId, { x: event.clientX, y: event.clientY });
    event.currentTarget.setPointerCapture(event.pointerId);
    if (this.#points.size === 2) {
      const [a, b] = this.#points.values();
      this.#pinchDistance = Math.hypot(a.x - b.x, a.y - b.y);
      this.#pinchZoom = this.#zoom;
    }
  };

  #pointerMove = (event) => {
    const previous = this.#points.get(event.pointerId);
    if (!previous) return;
    event.preventDefault();
    this.#points.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.#points.size === 2 && this.#pinchDistance > 0) {
      const [a, b] = this.#points.values();
      this.#setZoom(this.#pinchZoom * Math.hypot(a.x - b.x, a.y - b.y) / this.#pinchDistance);
    } else if (this.#zoom > 1) {
      event.currentTarget.scrollLeft -= event.clientX - previous.x;
      event.currentTarget.scrollTop -= event.clientY - previous.y;
    }
  };

  #pointerUp = (event) => {
    this.#points.delete(event.pointerId);
    this.#pinchDistance = 0;
  };

  #panKeys = (event) => {
    const moves = { ArrowLeft: [-50, 0], ArrowRight: [50, 0], ArrowUp: [0, -50], ArrowDown: [0, 50] };
    if (moves[event.key] && this.#zoom > 1) {
      event.preventDefault();
      const [left, top] = moves[event.key];
      event.currentTarget.scrollBy({ left, top, behavior: 'instant' });
    }
  };
}

if (!customElements.get('mindxbridge-projects')) {
  customElements.define('mindxbridge-projects', MindxbridgeProjects);
}
